import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { SectionHeader } from "./SectionHeader";
import { NumberInput } from "./NumberInput";
import { FactorControls } from "./FactorControls";
import type { PenaltySettings, RelevanceConfig } from "./types";

interface RelevanceScoringCardProps {
  relevanceWeight: number;
  onRelevanceWeightChange: (v: number) => void;
  penalties: PenaltySettings;
  onPenaltyChange: (key: keyof PenaltySettings, v: number) => void;
  relevance: RelevanceConfig;
  onToggleComponent: (comp: string) => void;
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
}

const PENALTY_ROWS: { key: keyof PenaltySettings; title: string; description: string }[] = [
  {
    key: "competitorBrandPenalty",
    title: "Competitor Brand Penalty",
    description: "Applied when a competitor brand is detected in the keyword. Reduces raw relevance before points are calculated.",
  },
  {
    key: "noOverlapPenalty",
    title: "No Lexical Overlap Penalty",
    description: "Applied when zero keyword tokens match the hero listing content.",
  },
];

const HERO_COMPONENTS = ["Title", "Brand", "Bullets", "Features", "Description"];

export function RelevanceScoringCard({
  relevanceWeight,
  onRelevanceWeightChange,
  penalties,
  onPenaltyChange,
  relevance,
  onToggleComponent,
  enabled,
  onEnabledChange,
}: RelevanceScoringCardProps) {
  return (
    <Card id="kps-module-relevance" className={cn(!enabled && "bg-muted/30 order-1")}>
      <CardContent className="pt-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <SectionHeader
            title="Relevance Scoring"
            description="Raw AI relevance score scales linearly to points. Penalty flags reduce the raw score before it's converted."
          />
          <FactorControls
            id="kps-toggle-relevance"
            enabled={enabled}
            onEnabledChange={onEnabledChange}
            value={relevanceWeight}
            onValueChange={onRelevanceWeightChange}
            step={0.5}
          />
        </div>

        <div className={cn("space-y-6", !enabled && "opacity-50 pointer-events-none select-none")}>
          <Alert variant="default">
            <AlertDescription className="space-y-1">
              <p className="text-sm font-semibold text-foreground">Formula</p>
              <p className="text-base tabular-nums text-foreground">
                <span className="text-muted-foreground">score = </span>
                <span className="font-semibold">adjusted_relevance</span>
                <span className="text-muted-foreground"> ÷ </span>
                <span className="font-semibold">100</span>
                <span className="text-muted-foreground"> × </span>
                <span className="font-semibold tabular-nums">{relevanceWeight}</span>
              </p>
            </AlertDescription>
          </Alert>

          <div className="space-y-0">
            <div className="grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 h-9 border-b border-border">
              <span className="text-xs font-semibold text-foreground">Raw Relevance</span>
              <span className="text-xs font-semibold text-foreground text-center">Points Earned</span>
              <span className="text-xs font-semibold text-foreground text-right">Score</span>
            </div>
            {[40, 65, 88, 100].map((pct) => {
              const max = relevanceWeight;
              const score = (pct / 100) * max;
              const fillPct = max > 0 ? (score / max) * 100 : 0;
              const formatted = Number.isInteger(score) ? score.toString() : score.toFixed(2).replace(/\.?0+$/, "");
              return (
                <div key={pct} className="grid grid-cols-[180px_1fr_100px] gap-4 items-center px-1 py-3 border-b border-border last:border-b-0">
                  <span className="text-sm text-foreground tabular-nums">{pct}%</span>
                  <Progress value={fillPct} className="h-2" indicatorClassName="bg-primary" />
                  <span className="text-sm tabular-nums text-right">
                    <span className="text-foreground">{formatted}</span>
                    <span className="text-xs text-muted-foreground"> / {max}</span>
                  </span>
                </div>
              );
            })}
          </div>

          <div className="space-y-3">
            <h4 className="text-lg font-semibold text-foreground flex items-center gap-2">
              Penalty Flags
              <Badge variant="secondary">Applied Before Scoring</Badge>
            </h4>
            {PENALTY_ROWS.map((row) => (
              <div key={row.key} className="flex items-start justify-between gap-6 py-3 border-b border-border last:border-b-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{row.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{row.description}</p>
                </div>
                <div className="shrink-0">
                  <NumberInput
                    value={penalties[row.key]}
                    onValueChange={(v) => onPenaltyChange(row.key, v)}
                    min={0}
                    max={1}
                    step={0.05}
                    className="w-20 h-8 text-sm text-center rounded-md bg-card tabular-nums"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <h4 className="text-lg font-semibold text-foreground">Hero Embedding Components</h4>
            <p className="text-sm text-muted-foreground">Select the fields used by Vertex AI to compute the hero listing embedding for similarity scoring.</p>
            <div className="flex flex-wrap gap-2 pt-1">
              {HERO_COMPONENTS.map((comp) => {
                const selected = relevance.heroComponents.includes(comp);
                return (
                  <button
                    key={comp}
                    type="button"
                    onClick={() => onToggleComponent(comp)}
                    className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                  >
                    <Badge variant={selected ? "default" : "outline"} className="text-xs cursor-pointer">
                      {comp}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
